import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

function formatTs(ts) {
  if (!ts?.seconds) return "N/A";
  return new Date(ts.seconds * 1000).toLocaleString();
}

function toCsv(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (value) => {
    const text = String(value ?? "");
    if (text.includes(",") || text.includes('"') || text.includes("\n")) {
      return `"${text.replaceAll('"', '""')}"`;
    }
    return text;
  };
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    lines.push(headers.map((h) => escape(row[h])).join(","));
  });
  return lines.join("\n");
}

export default function OwnerPayments({ user }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");

  const loadPayments = useCallback(async () => {
    if (!user?.email) {
      setBookings([]);
      return;
    }
    setLoading(true);
    try {
      setLoadError("");
      const ownerQ = query(collection(db, "bookings"), where("ownerEmail", "==", user.email));
      const legacyQ = query(collection(db, "bookings"), where("userEmail", "==", user.email));
      const [ownerSnap, legacySnap] = await Promise.all([getDocs(ownerQ), getDocs(legacyQ)]);
      const merged = [...ownerSnap.docs, ...legacySnap.docs];
      const byId = new Map(merged.map((d) => [d.id, { id: d.id, ...d.data() }]));
      setBookings([...byId.values()]);
    } catch (error) {
      setLoadError(error.message || "Unable to load payments.");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);
  useEffect(() => {
    if (!user?.email) {
      setBookings([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setLoadError("");

    const ownerRows = new Map();
    const legacyRows = new Map();
    let ownerReady = false;
    let legacyReady = false;

    const applyRows = () => {
      const merged = new Map([...ownerRows, ...legacyRows]);
      setBookings([...merged.values()]);
    };
    const finishLoading = () => {
      if (ownerReady && legacyReady) {
        setLoading(false);
      }
    };

    const ownerQuery = query(collection(db, "bookings"), where("ownerEmail", "==", user.email));
    const unsubscribeOwner = onSnapshot(
      ownerQuery,
      (snapshot) => {
        ownerReady = true;
        ownerRows.clear();
        snapshot.docs.forEach((item) => {
          ownerRows.set(item.id, { id: item.id, ...item.data() });
        });
        setLoadError("");
        applyRows();
        finishLoading();
      },
      (error) => {
        ownerReady = true;
        setLoadError(error.message || "Unable to load payments.");
        finishLoading();
      },
    );

    const legacyQuery = query(collection(db, "bookings"), where("userEmail", "==", user.email));
    const unsubscribeLegacy = onSnapshot(
      legacyQuery,
      (snapshot) => {
        legacyReady = true;
        legacyRows.clear();
        snapshot.docs.forEach((item) => {
          legacyRows.set(item.id, { id: item.id, ...item.data() });
        });
        setLoadError("");
        applyRows();
        finishLoading();
      },
      (error) => {
        legacyReady = true;
        setLoadError(error.message || "Unable to load legacy payments.");
        finishLoading();
      },
    );

    return () => {
      unsubscribeOwner();
      unsubscribeLegacy();
    };
  }, [user?.email]);

  const filtered = useMemo(() => {
    return bookings
      .filter((b) => (statusFilter ? String(b.status || "") === statusFilter : true))
      .filter((b) => (paymentFilter ? String(b.paymentStatus || "") === paymentFilter : true))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }, [bookings, statusFilter, paymentFilter]);

  const summary = useMemo(() => {
    const paid = filtered.filter((b) => b.paymentStatus === "paid");
    const settled = paid.filter((b) => b.refundStatus !== "refund_processed");
    const getAmount = (b) => Number(b.amount) || 0;
    const getCommission = (b) => Number(b.commissionAmount) || getAmount(b) * 0.2;
    const getProviderShare = (b) => Number(b.providerEarning) || getAmount(b) * 0.8;
    const totalPaid = paid.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    const totalSettled = settled.reduce((sum, b) => sum + getAmount(b), 0);
    const platformFee = settled.reduce((sum, b) => sum + getCommission(b), 0);
    const providerPayout = settled.reduce((sum, b) => sum + getProviderShare(b), 0);
    const refundedAmount = paid
      .filter((b) => b.refundStatus === "refund_processed")
      .reduce((sum, b) => sum + getAmount(b), 0);
    const refundRequested = filtered.filter((b) => b.refundStatus === "refund_requested").length;
    const refundProcessed = filtered.filter((b) => b.refundStatus === "refund_processed").length;
    return {
      totalPaid,
      totalSettled,
      platformFee,
      providerPayout,
      refundedAmount,
      count: filtered.length,
      refundRequested,
      refundProcessed,
    };
  }, [filtered]);

  const exportCsv = () => {
    const rows = filtered.map((b) => ({
      bookingId: b.id,
      serviceTitle: b.serviceTitle || b.serviceName || "Service",
      amount: Number(b.amount) || 0,
      commissionAmount: Number(b.commissionAmount) || (Number(b.amount) || 0) * 0.2,
      providerEarning: Number(b.providerEarning) || (Number(b.amount) || 0) * 0.8,
      paymentStatus: b.paymentStatus || "",
      bookingStatus: b.status || "",
      refundStatus: b.refundStatus || "not_requested",
      bookingDate: b.bookingDate || "",
      createdAt: formatTs(b.createdAt),
      paidAt: formatTs(b.paidAt),
      completedAt: formatTs(b.completedAt),
    }));
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `petaar-payments-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPaymentTone = (booking) => {
    const refund = String(booking.refundStatus || "").toLowerCase();
    const payment = String(booking.paymentStatus || "").toLowerCase();
    if (refund === "refund_processed") return "danger";
    if (payment === "paid") return "success";
    if (payment === "failed") return "danger";
    return "warning";
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Payments"
        title="Payments & Transactions"
        subtitle="Track booking payments, refunds, and settled amounts across your Petaar activity."
        actions={(
          <>
            <button className="petaar-button--secondary" onClick={exportCsv}>Export CSV</button>
            <button className="petaar-button" onClick={loadPayments} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </>
        )}
      />
      {loadError && (
        <div className="petaar-notice error" style={{ marginBottom: 16 }}>
          {loadError}
        </div>
      )}

      <div className="petaar-stat-grid" style={{ marginBottom: 20 }}>
        <StatCard icon="💸" label="Total Spent" value={summary.totalPaid} prefix="₹" decimals={2} tone="default" />
        <StatCard icon="🧾" label="Settled" value={summary.totalSettled} prefix="₹" decimals={2} tone="success" />
        <StatCard icon="↩️" label="Refunded" value={summary.refundedAmount} prefix="₹" decimals={2} tone="danger" />
        <StatCard icon="⏳" label="Pending Refunds" value={summary.refundRequested} tone="warning" />
      </div>

      <div className="petaar-card" style={{ marginBottom: 20 }}>
        <div className="petaar-toolbar compact">
          <div className="petaar-form-grid" style={{ width: "100%" }}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Booking Status</option>
          <option value="pending_assignment">pending_assignment</option>
          <option value="pending">pending</option>
          <option value="accepted">accepted</option>
          <option value="in_progress">in_progress</option>
          <option value="completed">completed</option>
          <option value="cancel_requested">cancel_requested</option>
          <option value="cancelled">cancelled</option>
        </select>
        <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
          <option value="">All Payment Status</option>
          <option value="paid">paid</option>
          <option value="pending">pending</option>
          <option value="failed">failed</option>
        </select>
        <button className="petaar-button--secondary" onClick={() => { setStatusFilter(""); setPaymentFilter(""); }}>
          Clear Filters
        </button>
          </div>
        </div>
      </div>

      <div className="petaar-grid two" style={{ marginBottom: 20 }}>
        <div className="petaar-card">
          <div className="petaar-kicker">Summary</div>
          <h3 style={{ marginTop: 4, marginBottom: 12 }}>Your spending overview</h3>
          <div className="petaar-list-card">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>Records</span>
              <strong>{summary.count}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>Platform fee</span>
              <strong>₹{summary.platformFee.toFixed(2)}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>Provider payout share</span>
              <strong>₹{summary.providerPayout.toFixed(2)}</strong>
            </div>
          </div>
        </div>
        <div className="petaar-card">
          <div className="petaar-kicker">Refund activity</div>
          <h3 style={{ marginTop: 4, marginBottom: 12 }}>Current refund state</h3>
          <div className="petaar-chip-row">
            <ToneBadge tone="warning">{summary.refundRequested} requested</ToneBadge>
            <ToneBadge tone="danger">{summary.refundProcessed} processed</ToneBadge>
          </div>
          <p style={{ color: "var(--muted)", marginTop: 14, marginBottom: 0 }}>
            Refunds appear automatically once booking status changes or a payment reversal is processed.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="petaar-card">Loading payment records...</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="💼"
          title="No transactions yet"
          description="Payments will appear here after you book services, buy subscriptions, or receive refunds."
        />
      ) : (
        <div className="petaar-list-card">
          {filtered.map((b) => (
            <div key={b.id} className="petaar-list-row">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div>
                  <strong style={{ display: "block", fontSize: "1.05rem" }}>{b.serviceTitle || b.serviceName || "Service"}</strong>
                  <small style={{ color: "var(--muted)" }}>
                    {b.bookingDate || "N/A"} • Paid at {formatTs(b.paidAt)}
                  </small>
                </div>
                <ToneBadge tone={getPaymentTone(b)}>
                  {b.paymentStatus || "unknown"}
                </ToneBadge>
              </div>
              <div className="petaar-grid two" style={{ gap: 14, marginTop: 16 }}>
                <div className="petaar-surface" style={{ padding: 16 }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span>Amount</span>
                      <strong>₹{(Number(b.amount) || 0).toFixed(2)}</strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span>Platform fee</span>
                      <strong>₹{(Number(b.commissionAmount) || (Number(b.amount) || 0) * 0.2).toFixed(2)}</strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span>Provider payout</span>
                      <strong>₹{(Number(b.providerEarning) || (Number(b.amount) || 0) * 0.8).toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
                <div className="petaar-surface" style={{ padding: 16 }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span>Booking status</span>
                      <ToneBadge tone={String(b.status || "").toLowerCase() === "completed" ? "success" : "default"}>
                        {b.status || "unknown"}
                      </ToneBadge>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span>Refund status</span>
                      <ToneBadge tone={String(b.refundStatus || "").toLowerCase() === "refund_processed" ? "danger" : "warning"}>
                        {b.refundStatus || "not_requested"}
                      </ToneBadge>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span>Completed at</span>
                      <strong>{formatTs(b.completedAt)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
